import React, { FC, useContext, useState } from 'react'
import styled from '@emotion/styled'
import { PlusIcon } from 'lucide-react'
import { ChatMobilePreviewTotal } from './ChatMobilePreviewTotal'
import { ChatMobilePreviewItem } from './ChatMobilePreviewItem'
import { ApiChatPreview } from '../../../api/chats/types/ApiChatPreview'
import { ChatMobileContext } from '../ChatMobileContext'
import { ChatMobilePreviewMaximizedToggle } from './ChatMobilePreviewMaximizedToggle'
import { PreviewEditsContext } from '../../../features/preview-edits/model/PreviewEditsContext'
import { PreviewAutocomplete } from '../../../features/preview-autocomplete'
import { ChatPreviewIconButton } from '../../preview/ChatPreviewIconButton'
import { AuthTokenFactory } from '../../../auth/AuthTokenFactory'
import { useTranslation } from 'react-i18next'

const MinimizedContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing.sm,
  flexBasis: '100%',
  overflow: 'auto',
}))

const MaximizedContainer = styled(MinimizedContainer)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  height: '100svh',
  padding: theme.spacing.sm,
  background: theme.colors.background.default,
}))

export const ChatMobilePreview: FC<{ preview: ApiChatPreview }> = ({ preview }) => {
  const { t } = useTranslation()
  const { previewMaximized } = useContext(ChatMobileContext)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const { items, adding, selectItem, removeItem, startAdding, cancelAdding, addItem, startEditingQuantity, stopEditingQuantity, updateUnit, updateQuantity, updateDate } = useContext(PreviewEditsContext)
  const isAgent = AuthTokenFactory.isAgent()
  const Container = previewMaximized ? MaximizedContainer : MinimizedContainer

  return (
    <>
      <Container>
        {items.map((item, index) => (
          <ChatMobilePreviewItem
            item={item}
            key={item.type === 'added' ? `added-${index}` : item.previewItem.uuid}
            editing={editingIndex === index}
            onEditToggle={() => setEditingIndex(editingIndex === index ? null : index)}
            onSelect={(selection) => {
              selectItem(index, selection)
              setEditingIndex(null)
            }}
            onRemove={() => removeItem(index)}
            onStartEditingQuantity={() => startEditingQuantity(index)}
            onStopEditingQuantity={() => stopEditingQuantity(index)}
            onUnitChange={(unitIndex) => updateUnit(index, unitIndex)}
            onQuantityChange={(quantity) => updateQuantity(index, quantity)}
            isAgent={isAgent}
            onDateChange={(date) => updateDate(index, date)}
          />
        ))}

        {adding ? (
          <PreviewAutocomplete autoFocus onCancel={cancelAdding} onSelect={addItem} />
        ) : (
          <ChatPreviewIconButton onClick={startAdding}>
            <PlusIcon size={16} /> {t('Add item')}
          </ChatPreviewIconButton>
        )}

        <ChatMobilePreviewTotal totalInfo={preview.totalInfo} />
      </Container>

      <ChatMobilePreviewMaximizedToggle />
    </>
  )
}
